import axios, {
  AxiosRequestConfig,
  AxiosError,
  Method,
  AxiosResponse,
  AxiosInstance,
} from "axios";
import { queryClient } from "../config/queryClient";
import { getRuntimeConfig } from "lib/runtimeEnvClient";

function getBaseURL(): string {
  // For client-side, use runtime config
  if (typeof window !== "undefined") {
    return getRuntimeConfig("NEXT_PUBLIC_AUTH_DOMAIN_URL");
  }

  // For server-side (SSR), fallback to process.env
  /* eslint-disable-next-line no-process-env */
  return process.env.NEXT_PUBLIC_AUTH_DOMAIN_URL || "";
}

// Lazy initialization - only create axios instance when first used
let _axiosAuthInstance: AxiosInstance | null = null;
let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (error: unknown) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    error ? prom.reject(error) : prom.resolve(token);
  });
  failedQueue = [];
};

function getAxiosAuthInstance(): AxiosInstance {
  if (_axiosAuthInstance) {
    return _axiosAuthInstance;
  }

  const baseURL = getBaseURL();

  if (!baseURL) {
    throw new Error(
      "Missing environment variable: NEXT_PUBLIC_AUTH_DOMAIN_URL"
    );
  }

  _axiosAuthInstance = axios.create({
    baseURL: baseURL,
    withCredentials: true,
    timeout: 15 * 60 * 1000,
  });

  _axiosAuthInstance.interceptors.request.use((config: any) => {
    const sessionId =
      typeof window !== "undefined"
        ? sessionStorage.getItem("sessionId")
        : null;

    return {
      ...config,
      headers: {
        ...config?.headers,
        "X-Session-Id": sessionId,
      },
    };
  });

  _axiosAuthInstance.interceptors.response.use(
    (res) => res,
    (err: AxiosError) => {
      const originalReq = err.config as any;
      const url = originalReq.url || "";

      const ignoreList = [
        "/auth/login",
        "/auth/request-otp",
        "/auth/refresh-token",
      ];

      if (!ignoreList.some((path) => url.endsWith(path))) {
        const originalReq = err.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        if (err.response?.status === 401 && !originalReq._retry) {
          originalReq._retry = true;

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(() => {
              return axiosAuth(originalReq);
            });
          }

          isRefreshing = true;

          return new Promise((resolve, reject) => {
            getAxiosAuthInstance()
              .post("/auth/refresh-token")
              .then(({ data }) => {
                const newToken = data.accessToken;
                processQueue(null, newToken);

                getAxiosAuthInstance()(originalReq)
                  .then((res) => {
                    void queryClient.invalidateQueries();
                    resolve(res);
                  })
                  .catch(reject);
              })
              .catch((refreshErr) => {
                processQueue(refreshErr, null);
                reject(refreshErr);
              })
              .finally(() => {
                isRefreshing = false;
              });
          });
        }

        return Promise.reject(err);
      } else {
        return Promise.reject(err);
      }
    }
  );

  return _axiosAuthInstance;
}

// Export getter instead of instance directly
export const axiosAuthInstance = {
  get instance() {
    return getAxiosAuthInstance();
  },
  post: (...args: Parameters<AxiosInstance["post"]>) =>
    getAxiosAuthInstance().post(...args),
  get: (...args: Parameters<AxiosInstance["get"]>) =>
    getAxiosAuthInstance().get(...args),
  put: (...args: Parameters<AxiosInstance["put"]>) =>
    getAxiosAuthInstance().put(...args),
  delete: (...args: Parameters<AxiosInstance["delete"]>) =>
    getAxiosAuthInstance().delete(...args),
  patch: (...args: Parameters<AxiosInstance["patch"]>) =>
    getAxiosAuthInstance().patch(...args),
  request: (...args: Parameters<AxiosInstance["request"]>) =>
    getAxiosAuthInstance().request(...args),
};

export interface OrvalRequestConfig extends AxiosRequestConfig {
  body?: any;
  query?: any;
}

export const axiosAuth = async <T = any>(
  config: OrvalRequestConfig
): Promise<T> => {
  const { url, method, data, query, ...rest } = config;

  const response: AxiosResponse<T> = await getAxiosAuthInstance().request<T>({
    url,
    method: method as Method,
    data: data,
    params: query,
    ...rest,
  });

  return response.data;
};

export default axiosAuth;
